import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useMissionsStore } from 'src/store/missions/missions.store';
import { questionsType } from 'src/types/missions';
import * as Yup from 'yup';

export default function useMissionDetails() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const STATUS_OPTIONS = ['active', 'inactive'];
  const STATUS_LABEL = ['Ativo', 'Inativo'];

  const TIERS_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'silver', label: 'Prata' },
    { value: 'gold', label: 'Ouro' },
    { value: 'diamond', label: 'Diamante' },
  ];

  const TYPE_OPTIONS = ['check-in', 'quiz', 'scan-count', 'instant'];
  const TYPE_LABEL = ['Check-in', 'Quiz', 'Scan de nota', 'Instantânea'];

  const INSTANT_TYPE_OPTIONS = ['nfScan', 'productRedeem', 'registerDate'];
  const INSTANT_TYPE_LABEL = ['Não escaneou NFs válidas', 'Não resgatou produtos', 'Data de cadastro VIVA'];

  const CORRECT_ANSWER_OPTIONS = ['answer1', 'answer2', 'answer3'];
  const CORRECT_ANSWER_LABEL = ['Resposta 1', 'Resposta 2', 'Resposta 3'];

  const ELEGIBLE_LABEL = ['Sim', 'Não'];
  const ELEGIBLE_OPTIONS = [true, false];
  const {
    getMissionDetails,
    resetMissionDetails,
    createMission,
    updateMission,
    isLoadingMissionDetails,
    missionDetails,
  } = useMissionsStore();

  useEffect(() => {
    if (id) {
      getMissionDetails(id);
    } else {
      resetMissionDetails();
    }
  }, [id, isEdit]);

  const mapQuestions = (questions: questionsType[] = []) =>
    questions.map((q) => {
      const correctIndex = q.answers.findIndex((answer) => answer === q.correctAnswer);

      const mappedCorrectAnswer = correctIndex !== -1 ? `answer${correctIndex + 1}` : '';

      return {
        question: q.question || '',
        answers: q.answers || ['', '', ''],
        correctAnswer: mappedCorrectAnswer,
      };
    });

  const defaultValues = useMemo(
    () => ({
      name: missionDetails?.name || '',
      description: missionDetails?.description || '',
      missionType: missionDetails?.missionType || '',
      startDate: missionDetails?.startDate ? new Date(missionDetails.startDate) : new Date(),
      endDate: missionDetails?.endDate ? new Date(missionDetails.endDate) : new Date(),
      status: missionDetails?.status || 'active',
      visibleForTiers: missionDetails?.visibleForTiers?.length
        ? missionDetails.visibleForTiers
        : ['all'],
      priority: missionDetails?.priority || 0,
      elegiblePoints: missionDetails?.elegiblePoints || false,
      pointsExpiration: missionDetails?.pointsExpiration || 30,
      rules: missionDetails?.rules?.map((r) => ({ ...r, startDate: new Date(r.startDate), endDate: new Date(r.endDate) })) || [],
      scanCountData: missionDetails?.scanCountData
        ? {
            requiredScans: missionDetails.scanCountData.requiredScans || 0,
            reward: missionDetails.scanCountData.reward || 0,
            detailsDescriptionTitle: missionDetails.scanCountData.detailsDescriptionTitle || '',
            detailsDescription: missionDetails.scanCountData.detailsDescription || '',
          }
        : {
            requiredScans: 0,
            reward: 0,
            detailsDescriptionTitle: '',
            detailsDescription: '',
          },

      quizData: missionDetails?.quizData
        ? {
            questions: mapQuestions(missionDetails.quizData.questions),
            chancesPerUser: 1,
            pointsPerQuestion: missionDetails.quizData.pointsPerQuestion || 0,
            timeToFinish: missionDetails.quizData.timeToFinish || 0,
            detailsDescription: missionDetails.quizData.detailsDescription || '',
            detailsDescriptionTitle: missionDetails.quizData.detailsDescriptionTitle || '',
          }
        : {
            questions: [],
            chancesPerUser: 1,
            pointsPerQuestion: 0,
            timeToFinish: 0,
            detailsDescription: '',
            detailsDescriptionTitle: '',
          },

      checkInData: missionDetails?.checkInData
        ? {
            daysInterval: missionDetails.checkInData.daysInterval || 0,
            stagesReward: missionDetails.checkInData.stagesReward || [],
            detailsDescription: missionDetails.checkInData.detailsDescription || '',
            detailsDescriptionTitle: missionDetails.checkInData.detailsDescriptionTitle || '',
            detailsTitle: missionDetails.checkInData.detailsTitle || '',
            secondaryDetailsTitle: missionDetails.checkInData.secondaryDetailsTitle || '',
          }
        : {
            daysInterval: 0,
            stagesReward: [],
            detailsDescription: '',
            detailsDescriptionTitle: '',
            detailsTitle: '',
            secondaryDetailsTitle: '',
          },

      instantData: missionDetails?.instantData ? 
          {
            detailsDescription: missionDetails?.instantData?.detailsDescription || '',
            detailsDescriptionTitle: missionDetails?.instantData?.detailsDescriptionTitle || '',
            reward: missionDetails?.instantData?.reward || 0,
            imageResult: missionDetails?.instantData?.imageResult || '',
          }
        : {
            detailsDescription: '',
            detailsDescriptionTitle: '',
            reward: 0,
            imageResult: '',
          },
    }),
    [isEdit, missionDetails]
  );

  const missionSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    description: Yup.string().required('Campo obrigatório!'),
    missionType: Yup.string().required('Campo obrigatório!'),
    startDate: Yup.date().required('Data de início é obrigatória'),
    endDate: Yup.date()
      .required('Data de término é obrigatória')
      .test(
        'is-minus-than-start',
        'Data de término deve ser maior que a data de início',
        (v, context) => {
          const { startDate } = context.parent;
          const start = new Date(startDate);
          const end = new Date(v);
          return end.getTime() >= start.getTime();
        }
      ),
    status: Yup.string().required('Campo obrigatório!'),
    visibleForTiers: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    priority: Yup.number().required('Campo obrigatório!'),
    pointsExpiration: Yup.number().min(1, 'Deve ser no mínimo 1').required('Campo obrigatório!'),
    elegiblePoints: Yup.boolean(),
    rules: Yup.array()
      .of(
        Yup.object().shape({
          ruleType: Yup.string().required('Campo obrigatório!'),
          startDate: Yup.date().required('Data de início é obrigatória'),
          endDate: Yup.date()
            .required('Data de término é obrigatória')
            .test(
              'is-minus-than-start',
              'Data de término deve ser maior que a data de início',
              (v, context) => {
                const { startDate } = context.parent;
                const start = new Date(startDate);
                const end = new Date(v);
                return end.getTime() >= start.getTime();
              }
            ),
        })
      ),
    scanCountData: Yup.object().when('missionType', {
      is: 'scan-count',
      then: () =>
        Yup.object().shape({
          requiredScans: Yup.number().min(1, 'Deve ser no mínimo 1').required('Campo obrigatório!'),
          reward: Yup.number()
            .min(1, 'Recompensa deve ser no mínimo 1')
            .required('Campo obrigatório!'),
          detailsDescriptionTitle: Yup.string().required('Campo obrigatório!'),
          detailsDescription: Yup.string().required('Campo obrigatório!'),
        }),
      otherwise: () => Yup.object().notRequired(),
    }),

    quizData: Yup.object().when('missionType', {
      is: 'quiz',
      then: () =>
        Yup.object().shape({
          questions: Yup.array()
            .of(
              Yup.object().shape({
                question: Yup.string().required('Campo obrigatório!'),
                answers: Yup.array()
                  .of(Yup.string().required('Resposta obrigatória!'))
                  .min(3, 'Deve haver pelo menos 3 respostas')
                  .max(3, 'Deve haver no máximo 3 respostas'),
                correctAnswer: Yup.string().required('Campo obrigatório!'),
              })
            )
            .min(1, 'Deve haver pelo menos uma questão'),
          chancesPerUser: Yup.number().min(1, 'Mínimo 1 chance').required('Campo obrigatório!'),
          pointsPerQuestion: Yup.number().min(1, 'Mínimo 1 ponto').required('Campo obrigatório!'),
          timeToFinish: Yup.number()
            .min(10, 'Deve ser pelo menos 10 segundos')
            .required('Campo obrigatório!'),
          detailsDescription: Yup.string().required('Campo obrigatório!'),
          detailsDescriptionTitle: Yup.string().required('Campo obrigatório!'),
        }),
      otherwise: () => Yup.object().notRequired(),
    }),

    checkInData: Yup.object().when('missionType', {
      is: 'check-in',
      then: () =>
        Yup.object().shape({
          daysInterval: Yup.number()
            .min(1, 'Intervalo deve ser pelo menos 1 dia')
            .required('Campo obrigatório!'),
          stagesReward: Yup.array()
            .of(Yup.number().min(1, 'A recompensa deve ser no mínimo 1'))
            .min(1, 'Deve haver pelo menos um estágio de recompensa'),
          detailsDescription: Yup.string().required('Campo obrigatório!'),
          detailsDescriptionTitle: Yup.string().required('Campo obrigatório!'),
          detailsTitle: Yup.string().required('Campo obrigatório!'),
          secondaryDetailsTitle: Yup.string().required('Campo obrigatório!'),
        }),
      otherwise: () => Yup.object().notRequired(),
    }),

    instantData: Yup.object().when('missionType', {
      is: 'instant',
      then: () =>
        Yup.object().shape({
          detailsDescriptionTitle: Yup.string().required('Campo obrigatório!'),
          detailsDescription: Yup.string().required('Campo obrigatório!'),
          reward: Yup.number()
            .min(1, 'Recompensa deve ser no mínimo 1')
            .required('Campo obrigatório!'),
          imageResult: Yup.mixed().required('Imagem é obrigatória'),
        }),
      otherwise: () => Yup.object().notRequired(),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(missionSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [missionDetails, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      const updatedData = {
        ...data,
        _id: id,
        visibleForTiers: data.visibleForTiers.includes('all') ? [] : data.visibleForTiers,
      };

      if (data.missionType === 'check-in') {
        delete updatedData.scanCountData;
        delete updatedData.quizData;
        delete updatedData.instantData;
      } else if (data.missionType === 'quiz') {
        delete updatedData.scanCountData;
        delete updatedData.checkInData;
        delete updatedData.instantData;

        updatedData.quizData.questions = data.quizData.questions.map((q: any) => {
          const correctIndex = ['answer1', 'answer2', 'answer3'].indexOf(q.correctAnswer);
          return {
            ...q,
            correctAnswer: correctIndex !== -1 ? q.answers[correctIndex] : '',
          };
        });
      } else if (data.missionType === 'scan-count') {
        delete updatedData.quizData;
        delete updatedData.checkInData;
        delete updatedData.instantData;
      } else if(data.missionType === 'instant'){
        delete updatedData.quizData;
        delete updatedData.checkInData;
        delete updatedData.scanCountData;
      }
      if (isEdit && id) {
        try {
          await updateMission(updatedData);
          enqueueSnackbar('Missão editada com sucesso!');
          navigate(paths.dashboard.missionsPage);
        } catch {
          enqueueSnackbar('Não foi possível editar a missão', { variant: 'error' });
        }
      } else {
        try {
          await createMission(updatedData);
          enqueueSnackbar('Missão criada com sucesso!');
          navigate(paths.dashboard.missionsPage);
        } catch {
          enqueueSnackbar('Não foi possível criar a missão.', { variant: 'success' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  });

  const start = watch('startDate');
  const end = watch('endDate');
  const tiers = watch('visibleForTiers');
  const type = watch('missionType');

  const {
    fields: quizFields,
    append: appendQuiz,
    remove: removeQuiz,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: 'quizData.questions',
  });

  useEffect(() => {
    if (quizFields.length === 0) {
      appendQuiz({
        question: '',
        answers: ['', '', ''],
        correctAnswer: '',
      });
    }
  }, [quizFields, appendQuiz]);

  const {
    fields: checkInFields,
    append: appendCheckIn,
    remove: removeCheckIn,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: 'checkInData.stagesReward',
  });

  useEffect(() => {
    if (checkInFields.length === 0) {
      appendCheckIn([0]);
    }
  }, [checkInFields, appendCheckIn]);

  const {
    fields: rulesFields,
    append: appendRules,
    remove: removeRules,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: 'rules',
  });

  const isValidEndDate = useMemo(() => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return endDate.getTime() >= startDate.getTime();
  }, [end, start]);

  useEffect(() => {
    if (Array.isArray(tiers) && tiers.includes('all') && tiers.length > 1) {
      setValue('visibleForTiers', ['all']);
    }
  }, [tiers]);

  const handleDrop = useCallback(
      (acceptedFiles: any) => {
        const file = acceptedFiles[0];
  
        if (file) {
          setValue(
            // @ts-ignore
            'instantData.imageResult',
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          );
        }
      },
      [setValue]
    );

  return {
    isLoadingMissionDetails,
    isSubmitting,
    STATUS_OPTIONS,
    STATUS_LABEL,
    TIERS_OPTIONS,
    TYPE_OPTIONS,
    TYPE_LABEL,
    isValidEndDate,
    isEdit,
    methods,
    control,
    type,
    quizFields,
    checkInFields,
    rulesFields,
    CORRECT_ANSWER_OPTIONS,
    CORRECT_ANSWER_LABEL,
    ELEGIBLE_LABEL,
    ELEGIBLE_OPTIONS,
    INSTANT_TYPE_OPTIONS,
    INSTANT_TYPE_LABEL,
    handleDrop,
    appendQuiz,
    removeQuiz,
    appendCheckIn,
    removeCheckIn,
    appendRules,
    removeRules,
    onSubmit,
  };
}
